// src/components/modals/ManageAllTagsModal.jsx
import React, { useState, useEffect } from 'react';
import { X, Tag, Plus, Loader2, Trash2, Edit2 } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { Badge } from '@/components/ui/badge.jsx';
import { apiCall } from '../../utils/api';

export default function ManageAllTagsModal({ isOpen, onClose, onUpdate }) {
  const [allTags, setAllTags] = useState([]);
  const [newTagName, setNewTagName] = useState('');
  const [editingTag, setEditingTag] = useState(null);
  const [editName, setEditName] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadTags();
    }
  }, [isOpen]);

  async function loadTags() {
    setLoading(true);
    try {
      const res = await apiCall('/charts/tags/');
      if (res.ok) {
        const data = await res.json();
        setAllTags(data.tags || []);
      }
    } catch (err) {
      console.error('Failed to load tags:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateTag() {
    if (!newTagName.trim()) {
      alert('Please enter a tag name');
      return;
    }

    setCreating(true);
    try {
      const res = await apiCall('/charts/tags/', {
        method: 'POST',
        body: JSON.stringify({ name: newTagName.trim() })
      });

      if (res.ok) {
        const newTag = await res.json();
        setAllTags([...allTags, newTag]);
        setNewTagName('');
        if (onUpdate) onUpdate();
      } else {
        const error = await res.json();
        alert(error.detail || 'Failed to create tag');
      }
    } catch (err) {
      console.error('Failed to create tag:', err);
      alert('Failed to create tag');
    } finally {
      setCreating(false);
    }
  }

  async function handleUpdateTag(tagId) {
    if (!editName.trim()) {
      alert('Please enter a tag name');
      return;
    }

    setUpdating(true);
    try {
      const res = await apiCall(`/charts/tags/${tagId}`, {
        method: 'PUT',
        body: JSON.stringify({ name: editName.trim() })
      });

      if (res.ok) {
        const updatedTag = await res.json();
        setAllTags(allTags.map(t => t.id === tagId ? updatedTag : t));
        setEditingTag(null);
        setEditName('');
        if (onUpdate) onUpdate();
      } else {
        const error = await res.json();
        alert(error.detail || 'Failed to update tag');
      }
    } catch (err) {
      console.error('Failed to update tag:', err);
      alert('Failed to update tag');
    } finally {
      setUpdating(false);
    }
  }

  async function handleDeleteTag(tagId, tagName) {
    if (!window.confirm(`Delete tag "${tagName}"? It will be removed from all charts.`)) {
      return;
    }

    try {
      const res = await apiCall(`/charts/tags/${tagId}`, {
        method: 'DELETE'
      });

      if (res.status === 204 || res.ok) {
        setAllTags(allTags.filter(t => t.id !== tagId));
        if (onUpdate) onUpdate();
      } else {
        alert('Failed to delete tag');
      }
    } catch (err) {
      console.error('Failed to delete tag:', err);
      alert('Failed to delete tag');
    }
  }

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div 
          className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-[#2a2a2a]">
            <div className="flex items-center gap-2">
              <Tag className="w-5 h-5 text-blue-500" />
              <h2 className="text-xl font-semibold text-white">Manage Tags</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-[#2a2a2a] rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-[#a0a0a0]" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
              </div>
            ) : (
              <div className="space-y-6">
                {/* Create New Tag */}
                <div>
                  <h3 className="text-sm font-medium text-white mb-3">
                    Create New Tag
                  </h3>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Tag name..."
                      value={newTagName}
                      onChange={(e) => setNewTagName(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleCreateTag()}
                      className="flex-1 h-10 px-3 bg-[#0a0a0a] border border-[#2a2a2a] text-white text-sm rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 placeholder:text-[#4a4a4a]"
                    />
                    <Button onClick={handleCreateTag} disabled={creating || !newTagName.trim()}>
                      {creating ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Plus className="w-4 h-4" />
                          Create
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* All Tags List */}
                <div className="pt-4 border-t border-[#2a2a2a]">
                  <h3 className="text-sm font-medium text-white mb-3">
                    All Tags ({allTags.length})
                  </h3>
                  {allTags.length > 0 ? (
                    <div className="space-y-2">
                      {allTags.map(tag => (
                        <div 
                          key={tag.id}
                          className="flex items-center justify-between p-3 bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg"
                        >
                          {editingTag === tag.id ? (
                            <>
                              <input
                                type="text"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleUpdateTag(tag.id)}
                                className="flex-1 h-8 px-2 bg-[#1a1a1a] border border-[#2a2a2a] text-white text-sm rounded focus:outline-none focus:border-blue-500"
                                autoFocus
                              />
                              <div className="flex items-center gap-2 ml-2">
                                <button
                                  onClick={() => handleUpdateTag(tag.id)}
                                  disabled={updating}
                                  className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                                >
                                  {updating ? 'Saving...' : 'Save'}
                                </button>
                                <button
                                  onClick={() => {
                                    setEditingTag(null);
                                    setEditName('');
                                  }}
                                  className="text-xs text-[#6a6a6a] hover:text-white transition-colors"
                                >
                                  Cancel
                                </button>
                              </div>
                            </>
                          ) : (
                            <>
                              <Badge variant="neutral">{tag.name}</Badge>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => {
                                    setEditingTag(tag.id);
                                    setEditName(tag.name);
                                  }}
                                  className="p-2 text-[#a0a0a0] hover:bg-[#2a2a2a] hover:text-white rounded transition-colors"
                                  title="Edit tag"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteTag(tag.id, tag.name)}
                                  className="p-2 text-red-400 hover:bg-red-500/10 rounded transition-colors"
                                  title="Delete tag"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-[#6a6a6a]">No tags created yet</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-[#2a2a2a]">
            <Button onClick={onClose}>
              Done
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}